import type { Schema } from "@/api/types";
import type { Candle, Overlay, SeriesSpec } from "@/components/chart";
import { AVERAGES, type AverageId } from "@/components/chart/averages";

import { strikeText } from "./chain";

export type Premium = Schema<"PremiumOut">;
export type PremiumBar = Schema<"PremiumBar">;

export const PREMIUM_INTERVALS = ["1m", "5m", "15m", "day"] as const;
export type PremiumInterval = (typeof PREMIUM_INTERVALS)[number];
/** Options live weeks, so the short averages are the useful ones (SMA 100/200 rarely fill). */
export const PREMIUM_AVERAGES: readonly AverageId[] = ["sma20", "sma50", "ema9", "ema21"];

export interface PremiumSearch {
  symbol: string;
  /** ISO date; with `strike` and `side` it names the contract. */
  expiry?: string;
  strike?: number;
  side: "CE" | "PE";
  interval: PremiumInterval;
}

export function parsePremiumSearch(s: Record<string, unknown>): PremiumSearch {
  const symbol = typeof s.symbol === "string" && /^[A-Za-z0-9&_-]{1,30}$/.test(s.symbol) ? s.symbol.toUpperCase() : "NIFTY";
  const expiry = typeof s.expiry === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.expiry) ? s.expiry : undefined;
  const k = Number(s.strike);
  const strike = Number.isFinite(k) && k > 0 ? k : undefined;
  const side = s.side === "PE" ? "PE" : "CE";
  const interval = PREMIUM_INTERVALS.includes(s.interval as PremiumInterval) ? (s.interval as PremiumInterval) : "5m";
  return { symbol, ...(expiry ? { expiry } : {}), ...(strike ? { strike } : {}), side, interval };
}

/** "NIFTY 23,000 CE". */
export function contractTitle(symbol: string, strike: number, side: string): string {
  return `${symbol} ${strikeText(strike)} ${side}`;
}

export function premiumCandles(bars: readonly PremiumBar[]): Candle[] {
  return bars.map((b) => ({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume }));
}

/** The chosen averages, as the server worked them out (over the contract's whole history). */
export function premiumOverlays(bars: readonly PremiumBar[], shown: readonly string[]): Overlay[] {
  return AVERAGES.filter((a) => shown.includes(a.id) && (PREMIUM_AVERAGES as readonly string[]).includes(a.id)).map((a) => ({
    id: a.id,
    title: a.label,
    color: a.color,
    lineWidth: 2,
    data: bars.map((b) => ({ time: b.time, value: (b[a.id as keyof PremiumBar] as number | null | undefined) ?? null })),
  }));
}

/** Open interest above this is charted in lakh, so the axis reads 23.5 rather than 2350335. */
const LAKH = 100_000;

/** Open interest per bar (in lakh when large), or null when no bar has it. */
export function oiSeries(bars: readonly PremiumBar[]): { spec: SeriesSpec; lakh: boolean } | null {
  if (!bars.some((b) => b.oi != null)) return null;
  const lakh = Math.max(...bars.map((b) => b.oi ?? 0)) >= 10 * LAKH;
  return {
    lakh,
    spec: {
      type: "area",
      title: "OI",
      color: "brand",
      data: bars.map((b) => ({ time: b.time, value: b.oi == null ? null : lakh ? b.oi / LAKH : b.oi })),
      priceFormat: lakh ? { precision: 2, minMove: 0.01 } : { precision: 0, minMove: 1 },
      scaleMargins: { top: 0.15, bottom: 0.05 },
    },
  };
}
